import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import RetailAccountCard from '@shared/RetailAccountCard';
import { sortRetailAccountsLiveFirst } from '@shared/sortRetailAccounts';
import { UserRetailAccount } from '@shared/types';
import {
  useRetailAccounts as useRetailAccountsQuery,
  useAccountContentQueries,
  usePatchAccountMutation,
  useRebalanceMutation,
} from '../../queries/retailAccounts';
import { retailAccountKeys } from '../../queries/queryKeys';
import { hasActivePortfolio } from '../../hooks/useRetailAccounts';
import ConnectAlpacaForm from '../onboarding/ConnectAlpacaForm';
import RetailAccountCardToolbar from './RetailAccountCardToolbar';
import {
  PortfolioCompareCardButton,
  PortfolioCompareDock,
  compareCardWrapClass,
  dockPlFromContent,
} from '@shared/PortfolioCompareUx';

// Token-class equivalents of the legacy ProfilePage.css / onboarding.css
// remnants this component used to depend on.
const PROFILE_CARD =
  'rounded-xl border border-solid border-[color:var(--border-slate)] bg-[color:var(--nq-surface-card-2)] px-6 py-5 shadow-[var(--shadow-card-soft),var(--inset-card-glow)] max-md:px-5 max-md:py-4';
const PROFILE_CARD_TITLE =
  'mb-4 mt-0 font-display text-[length:var(--text-h3)] font-bold tracking-[-0.02em] text-foreground';
const PROFILE_CARD_LEAD = 'mb-4 mt-0 text-sm leading-normal text-muted-foreground';
// Bottom padding so the floating compare dock never covers the last cards
// (was .nq-compare-page-pad).
const DOCK_PAD =
  'pb-[calc(148px+env(safe-area-inset-bottom,0px))] max-[640px]:pb-[calc(168px+env(safe-area-inset-bottom,0px))]';

type Props = {
  variant?: 'profile' | 'onboarding';
  onAccountsChange?: () => void;
  onLinked?: () => void | Promise<void>;
  connectSubmitLabel?: string;
  connectSubmitting?: boolean;
};

const extractError = (err: unknown) =>
  (err as { response?: { data?: { error?: string } } }).response?.data?.error ?? undefined;

const RetailAccountManager = ({
  variant = 'profile',
  onAccountsChange,
  onLinked,
  connectSubmitLabel,
  connectSubmitting = false,
}: Props) => {
  const isOnboarding = variant === 'onboarding';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [selectedForCompare, setSelectedForCompare] = useState<Set<string>>(() => new Set());

  // Per-entity busy state — useMutation.isPending is global; we track which account is in-flight
  const [updatingRetailAccountId, setUpdatingRetailAccountId] = useState<string | null>(null);
  const [rebalancingRetailAccountId, setRebalancingRetailAccountId] = useState<string | null>(null);
  const [togglingSignalForgeId, setTogglingSignalForgeId] = useState<string | null>(null);

  const { data: userRetailAccounts = [], isLoading: retailAccountsLoading } = useRetailAccountsQuery();

  // Load content for all accounts eagerly in profile mode; skip entirely during onboarding
  const enabledIds = useMemo(
    () => (isOnboarding ? new Set<string>() : new Set(userRetailAccounts.map((a) => a.id))),
    [isOnboarding, userRetailAccounts],
  );
  const contentQueries = useAccountContentQueries(userRetailAccounts, enabledIds);

  const retailAccountContentById = useMemo(() => {
    const map: Record<string, import('@shared/types').RetailAccountContent | undefined> = {};
    userRetailAccounts.forEach((acc, i) => { map[acc.id] = contentQueries[i]?.data; });
    return map;
  }, [userRetailAccounts, contentQueries]);

  const retailAccountContentLoadingById = useMemo(() => {
    const map: Record<string, boolean> = {};
    userRetailAccounts.forEach((acc, i) => { map[acc.id] = !!contentQueries[i]?.isFetching; });
    return map;
  }, [userRetailAccounts, contentQueries]);

  const retailAccountContentErrorById = useMemo(() => {
    const map: Record<string, string | null> = {};
    userRetailAccounts.forEach((acc, i) => {
      const q = contentQueries[i];
      map[acc.id] = q?.error ? (extractError(q.error) ?? 'Failed to load retail account content') : null;
    });
    return map;
  }, [userRetailAccounts, contentQueries]);

  const retailAccountContentUpdatedAtById = useMemo(() => {
    const map: Record<string, Date> = {};
    userRetailAccounts.forEach((acc, i) => {
      const updatedAt = contentQueries[i]?.dataUpdatedAt;
      if (updatedAt) map[acc.id] = new Date(updatedAt);
    });
    return map;
  }, [userRetailAccounts, contentQueries]);

  const patchAccount = usePatchAccountMutation();
  const rebalance = useRebalanceMutation();

  const handleToggleRetailAccountActive = useCallback(async (account: UserRetailAccount) => {
    setUpdatingRetailAccountId(account.id);
    try {
      await patchAccount.mutateAsync({ id: account.id, patch: { active: !account.active } });
      toast.success(`Retail account ${account.active ? 'deactivated' : 'activated'}`);
      onAccountsChange?.();
    } catch (error: unknown) {
      toast.error(extractError(error) ?? 'Failed to update retail account');
    } finally {
      setUpdatingRetailAccountId(null);
    }
  }, [patchAccount, onAccountsChange]);

  const handleToggleSignalForge = useCallback(async (account: UserRetailAccount) => {
    setTogglingSignalForgeId(account.id);
    try {
      const nextValue = !account.signal_forge_enabled;
      await patchAccount.mutateAsync({ id: account.id, patch: { signalForgeEnabled: nextValue } });
      toast.success(`SignalForge ${nextValue ? 'enabled' : 'disabled'}`);
      onAccountsChange?.();
    } catch (error: unknown) {
      toast.error(extractError(error) ?? 'Failed to update SignalForge');
    } finally {
      setTogglingSignalForgeId(null);
    }
  }, [patchAccount, onAccountsChange]);

  const handleRebalanceRetailAccount = useCallback(async (accountId: string) => {
    setRebalancingRetailAccountId(accountId);
    try {
      await rebalance.mutateAsync(accountId);
      toast.success('Rebalance triggered successfully');
    } catch (error: unknown) {
      toast.error(extractError(error) ?? 'Failed to trigger rebalance');
    } finally {
      setRebalancingRetailAccountId(null);
    }
  }, [rebalance]);

  const handleRefreshContent = useCallback(async (accountId: string) => {
    await queryClient.invalidateQueries({ queryKey: retailAccountKeys.content(accountId) });
  }, [queryClient]);

  const handleLinked = useCallback(async () => {
    // Accounts list already invalidated by useConnectAlpacaMutation onSuccess
    onAccountsChange?.();
    await onLinked?.();
  }, [onAccountsChange, onLinked]);

  const needsActiveAccount =
    isOnboarding &&
    userRetailAccounts.length > 0 &&
    !hasActivePortfolio(userRetailAccounts);

  const showAccountsSection =
    !isOnboarding || userRetailAccounts.length > 0 || retailAccountsLoading;

  const canCompare = !isOnboarding && userRetailAccounts.length >= 2;
  const selectedIds = useMemo(() => Array.from(selectedForCompare), [selectedForCompare]);

  const portfolioLabel = (acc: UserRetailAccount) =>
    acc.nickname ||
    retailAccountContentById[acc.id]?.accountName ||
    (acc.isPaper ? 'Paper Trading' : 'Live Trading');

  const dockItems = useMemo(
    () =>
      selectedIds.map((id) => {
        const acc = userRetailAccounts.find((a) => a.id === id);
        const content = retailAccountContentById[id];
        const { plAmount, plPercent } = dockPlFromContent(content);
        return { id, label: acc ? portfolioLabel(acc) : id, plAmount, plPercent };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedIds, userRetailAccounts, retailAccountContentById],
  );

  const toggleCompareSelection = (accountId: string) => {
    setSelectedForCompare((prev) => {
      const next = new Set(prev);
      if (next.has(accountId)) next.delete(accountId);
      else next.add(accountId);
      return next;
    });
  };

  const clearCompareSelection = () => setSelectedForCompare(new Set());

  const openComparison = () => {
    if (selectedIds.length < 2) {
      toast.error('Pick at least 2 portfolios to compare');
      return;
    }
    navigate(`/profile/portfolios/compare/view?ids=${selectedIds.join(',')}`);
  };

  return (
    <div
      className={[
        isOnboarding ? undefined : 'flex flex-col gap-5',
        canCompare && selectedIds.length > 0 ? DOCK_PAD : undefined,
      ]
        .filter(Boolean)
        .join(' ') || undefined}
    >
      {showAccountsSection && (
        <div className={isOnboarding ? undefined : PROFILE_CARD}>
          {!isOnboarding && (
            <h2 className={PROFILE_CARD_TITLE}>My Portfolios</h2>
          )}
          {isOnboarding && userRetailAccounts.length > 0 && (
            <h2 className="mb-2.5 mt-0 font-display text-xl font-bold leading-[1.1] tracking-[-0.02em] text-secondary [text-shadow:var(--glow-gold-text)]">
              Your linked accounts
            </h2>
          )}

          {needsActiveAccount && (
            <p className="mb-4 mt-0 text-sm leading-normal text-[color:var(--text-secondary)]">
              Activate at least one account to continue.
            </p>
          )}

          <div className="portfolios-list">
            {retailAccountsLoading ? (
              <p className="loading-text">Loading Alpaca market accounts...</p>
            ) : userRetailAccounts.length === 0 ? (
              !isOnboarding && (
                <p className={PROFILE_CARD_LEAD}>No linked Alpaca market accounts yet.</p>
              )
            ) : (
              sortRetailAccountsLiveFirst(userRetailAccounts).map((acc) => {
                const isSelectedForCompare = selectedForCompare.has(acc.id);
                // `relative mt-[18px]` replaces the base `.nq-compare-card-wrap`
                // chrome (anchors the floating compare button + leaves overhang
                // room) now that PortfolioCompareUx.css is no longer imported here.
                const cardWrapperClass = canCompare
                  ? `relative mt-[18px] ${compareCardWrapClass(isSelectedForCompare, acc.isPaper)}`
                  : undefined;
                return (
                  <div key={acc.id} className={cardWrapperClass}>
                    {canCompare && (
                      <PortfolioCompareCardButton
                        selected={isSelectedForCompare}
                        onToggle={() => toggleCompareSelection(acc.id)}
                      />
                    )}
                    <RetailAccountCard
                      account={acc}
                      content={retailAccountContentById[acc.id]}
                      contentLoading={!!retailAccountContentLoadingById[acc.id]}
                      contentError={retailAccountContentErrorById[acc.id]}
                      onLoadContentIfNeeded={() => {
                        if (isOnboarding) return;
                        if (!retailAccountContentById[acc.id] && !retailAccountContentLoadingById[acc.id]) {
                          void handleRefreshContent(acc.id);
                        }
                      }}
                      actions={
                        !isOnboarding ? (
                          <RetailAccountCardToolbar
                            account={acc}
                            refreshing={!!retailAccountContentLoadingById[acc.id]}
                            rebalancing={rebalancingRetailAccountId === acc.id}
                            updating={updatingRetailAccountId === acc.id}
                            togglingSignalForge={togglingSignalForgeId === acc.id}
                            onDeactivate={() => handleToggleRetailAccountActive(acc)}
                            onRefresh={() => handleRefreshContent(acc.id)}
                            onToggleSignalForge={() => handleToggleSignalForge(acc)}
                            onRebalance={() => handleRebalanceRetailAccount(acc.id)}
                            lastUpdatedAt={retailAccountContentUpdatedAtById[acc.id]}
                          />
                        ) : (
                          <button
                            type="button"
                            className={`account-action-btn ${acc.active ? 'account-action-btn--caution' : 'account-action-btn--primary'}`}
                            onClick={() => handleToggleRetailAccountActive(acc)}
                            disabled={updatingRetailAccountId === acc.id}
                          >
                            {updatingRetailAccountId === acc.id
                              ? 'Updating...'
                              : acc.active
                                ? 'Deactivate'
                                : 'Activate'}
                          </button>
                        )
                      }
                    />
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      <div className={isOnboarding ? undefined : PROFILE_CARD}>
        {!isOnboarding && (
          <>
            <h2 className={PROFILE_CARD_TITLE}>Add New Account</h2>
            <p className={PROFILE_CARD_LEAD}>
              Connect an existing Alpaca account using API key and secret.
            </p>
          </>
        )}

        <ConnectAlpacaForm
          onLinked={handleLinked}
          submitLabel={connectSubmitLabel}
          submitting={connectSubmitting}
        />
      </div>

      {canCompare && (
        <PortfolioCompareDock
          items={dockItems}
          onRemove={toggleCompareSelection}
          onClear={clearCompareSelection}
          onCompare={openComparison}
        />
      )}
    </div>
  );
};

export default RetailAccountManager;
